import { useRef } from 'react';
import { ChevronLeft, ChevronRight, RefreshCw, Calendar as CalendarIcon } from 'lucide-react';
import { formatFriendlyDate, today } from '@/utils/date';
import dayjs from 'dayjs';

export interface DateNavigatorProps {
  selectedDate: string;
  onDateChange: (date: string) => void;
  onRefresh?: () => void;
  loading?: boolean;
}

export function DateNavigator({
  selectedDate,
  onDateChange,
  onRefresh,
  loading = false,
}: DateNavigatorProps) {
  const dateInputRef = useRef<HTMLInputElement>(null);

  const goToOffset = (offset: number) => {
    const next = dayjs(selectedDate).add(offset, 'day').format('YYYY-MM-DD');
    onDateChange(next);
  };

  const isToday = selectedDate === today();

  return (
    <div className="flex items-center justify-between gap-2 p-2 bg-surface border border-border rounded-xl shadow-xs">
      <button
        type="button"
        onClick={() => goToOffset(-1)}
        className="p-2 rounded-lg hover:bg-surface-secondary text-text-secondary transition-colors cursor-pointer"
        title="Día anterior"
      >
        <ChevronLeft size={18} />
      </button>

      <div className="flex items-center gap-2 relative">
        {/* Invisible native date input for high-fidelity popover across all devices */}
        <input
          ref={dateInputRef}
          type="date"
          value={selectedDate}
          onChange={(e) => e.target.value && onDateChange(e.target.value)}
          className="absolute inset-0 opacity-0 cursor-pointer pointer-events-auto w-full"
          tabIndex={-1}
        />

        <button
          type="button"
          onClick={() => dateInputRef.current?.showPicker?.() || dateInputRef.current?.click()}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-surface-secondary transition-colors text-center cursor-pointer group"
        >
          <CalendarIcon size={15} className="text-brand-primary group-hover:scale-110 transition-transform" />
          <span className="text-xs sm:text-sm font-semibold text-text-primary capitalize">
            {formatFriendlyDate(selectedDate)}
          </span>
          <span className="text-[10px] text-text-muted hidden sm:inline">({selectedDate})</span>
        </button>

        {!isToday && (
          <button
            type="button"
            onClick={() => onDateChange(today())}
            className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-brand-primary/10 text-brand-primary hover:bg-brand-primary/20 rounded-md transition-colors cursor-pointer"
          >
            Hoy
          </button>
        )}
      </div>

      <div className="flex items-center gap-1">
        {onRefresh && (
          <button
            type="button"
            onClick={onRefresh}
            className="p-2 rounded-lg hover:bg-surface-secondary text-text-secondary transition-colors cursor-pointer"
            title="Recargar citas"
          >
            <RefreshCw size={15} className={loading ? 'animate-spin text-brand-primary' : ''} />
          </button>
        )}
        <button
          type="button"
          onClick={() => goToOffset(1)}
          className="p-2 rounded-lg hover:bg-surface-secondary text-text-secondary transition-colors cursor-pointer"
          title="Día siguiente"
        >
          <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );
}
